import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  TextField,
  Breadcrumbs,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  ImageList,
  ImageListItem,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useCart } from "../contexts/CartContext";
import { useAppContext } from "../contexts/AppContext";
import AddToCartSuccessModal from "../components/Cart/AddToCartSuccessModal";
import ReactMarkdown from 'react-markdown';
import logger from '../utils/logger';
import {
  fetchProductById,
  fetchProducts,
  getProductImageUrl,
  formatCurrency,
} from "../services/productService";
import { supabase } from '../lib/supabase';
import { ENABLE_CHECKOUT } from '../config/featureFlags';
import BuyOnEtsyButton from '../components/Products/BuyOnEtsyButton';

// Import components
import ProductOptionsSelector from "../components/Products/ProductOptionsSelector";
import ProductSpecifications from "../components/Products/ProductSpecifications";
import ProductPersonalization from "../components/Products/ProductPersonalization";

// Import utilities
import { updateMetaTags, createProductStructuredData, updateStructuredData } from "../utils/seoUtils";

/**
 * Helper function to generate a concise slug from a product name and category
 */
const generateConciseSlug = (name, category = "") => {
  let slug = category ? `${category.toLowerCase()}-` : "";
  const simpleName = name.split(/[,:|-]/)[0].trim();

  slug += simpleName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .substring(0, 60);

  return slug;
};

/**
 * Generate a random alphanumeric code of the given length.
 */
const randomCode = (length = 7) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Build and persist a short link for the current product + options state.
 * Inserts a row into the `short_links` table and returns the short URL.
 *
 * @param {Object} product
 * @param {Object} selectedOptions  - { [variationName]: valueTitle }
 * @param {string} personalizationText
 * @returns {Promise<string>} short URL
 */
const createShortLink = async (product, selectedOptions, personalizationText) => {
  const productId = product.slug || product.id;
  const params = new URLSearchParams();
  if (Object.keys(selectedOptions).length > 0) {
    params.set('options', JSON.stringify(selectedOptions));
  }
  if (personalizationText) {
    params.set('personalization', personalizationText);
  }
  const targetPath = `/products/${productId}${params.toString() ? '?' + params.toString() : ''}`;

  const code = randomCode(7);
  const { error } = await supabase.from('short_links').insert({
    code,
    target_url: targetPath,
  });

  if (error) {
    // If insert fails (e.g. table doesn't exist yet), fall back to full URL
    logger.warn('short_links insert failed, using full URL', { error: error.message });
    return window.location.origin + targetPath;
  }

  return `${window.location.origin}/l/${code}`;
};

/**
 * @fileoverview Product Detail Page
 *
 * This page displays detailed information about a specific product,
 * including images, description, pricing, and customization options.
 *
 * Key features:
 * - Displays product gallery from Supabase Storage images
 * - Interactive option selection with ProductOptionsSelector
 * - Personalization field for custom text
 * - Share button that creates a short link encoding current options/personalization
 * - Buy on Etsy CTA links to product listing or shop search fallback
 * - Cart/checkout hidden behind ENABLE_CHECKOUT feature flag
 *
 * @module pages/ProductDetail
 */
const ProductDetail = () => {
  // URL parameter
  const { idOrSlug } = useParams();
  const location = useLocation();

  // Core state
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [personalizationText, setPersonalizationText] = useState("");

  // Share state
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  // Ref for option selector validation
  const optionsSelectorRef = useRef(null);

  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [addedProduct, setAddedProduct] = useState(null);
  const [modalOptions, setModalOptions] = useState({});

  // Context hooks
  const { addToCart } = useCart();
  const { showSnackbar } = useAppContext();

  /**
   * Query function for React Query
   * Handles fetching product by ID or slug with fallback to concise slug
   */
  const queryFn = async () => {
    try {
      // First try direct fetch by ID or slug
      const data = await fetchProductById(idOrSlug);
      if (data) return data;
      throw new Error("Product not found");
    } catch (initialError) {
      // Try fallback to concise slug
      const allProducts = await fetchProducts();
      const matchingProduct = allProducts.find((p) => {
        const category = p.categories?.[0] || "";
        const conciseSlug = generateConciseSlug(p.name, category);
        return conciseSlug === idOrSlug;
      });

      if (matchingProduct) {
        const data = await fetchProductById(matchingProduct.id);
        if (data) return data;
      }

      throw new Error("Product not found or could not be loaded");
    }
  };

  // Fetch product data
  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["product", idOrSlug],
    queryFn,
    staleTime: 5 * 60 * 1000,
  });

  // Derived: personalization is valid when not required, or when text is present
  const personalizationValid = !product
    || product?.personalization?.enabled !== 'required'
    || !!personalizationText.trim();

  // Restore options and personalization from shared link query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const optionsParam = params.get('options');
    const personalizationParam = params.get('personalization');

    if (optionsParam) {
      try {
        const parsed = JSON.parse(optionsParam);
        if (parsed && typeof parsed === 'object') {
          setSelectedOptions(parsed);
        }
      } catch (e) {
        logger.warn('Failed to parse options query param', { error: e.message });
      }
    }

    if (personalizationParam) {
      setPersonalizationText(personalizationParam);
    }
  // Only run once on mount (location.search won't change during the lifecycle of this page visit)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update SEO meta tags and structured data when product changes
  useEffect(() => {
    if (!product) return;

    logger.debug('Product loaded', {
      productName: product.name,
    });

    // Create structured data
    const productData = createProductStructuredData(product, null, getProductImageUrl);

    // Update meta tags
    updateMetaTags({
      title: product?.meta?.title || product?.name || "Product | KBros Leather",
      description: product?.meta?.description || product?.shortDescription || "",
      keywords: product?.meta?.keywords || (product?.tags ? product.tags.join(", ") : ""),
    });

    // Update structured data
    updateStructuredData(productData);
  }, [product]);

  // Reset selected image when product changes
  useEffect(() => {
    if (product && product.images && product.images.length > 0) {
      setSelectedImage(0);
    }
  }, [product]);

  /**
   * Handle quantity change
   */
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value > 0) setQuantity(value);
  };

  /**
   * Handle tab change
   */
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  /**
   * Handle adding product to cart
   */
  const handleAddToCart = () => {
    // Validate option selections
    if (optionsSelectorRef.current) {
      const valid = optionsSelectorRef.current.validate();
      if (!valid) {
        showSnackbar("Please select all required options", "error");
        return;
      }
    }

    // Validate personalization
    if (!personalizationValid) {
      showSnackbar("Please complete the personalization field", "error");
      return;
    }

    // Create product object for cart
    const productForCart = {
      ...product,
      price: product.price,
      image: product.images?.[0] ? getProductImageUrl(product.images[0]) : '',
      inStock: true,
    };

    const trimmedText = personalizationText.trim();

    // Add to cart
    addToCart(productForCart, quantity, selectedOptions, trimmedText);

    // Show success modal
    setAddedProduct(productForCart);
    setModalOptions(selectedOptions);
    setSuccessModalOpen(true);
  };

  /**
   * Handle share / copy link
   */
  const handleShare = async () => {
    if (!product) return;
    setShareLoading(true);
    try {
      const url = await createShortLink(product, selectedOptions, personalizationText);
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      showSnackbar("Link copied!", "success");
      // Auto-clear the stored URL after 3 seconds (cosmetic only)
      setTimeout(() => setShareUrl(null), 3000);
    } catch (err) {
      logger.error('Share failed', { error: err.message });
      // Fallback: copy the current page URL
      try {
        await navigator.clipboard.writeText(window.location.href);
        showSnackbar("Link copied!", "success");
      } catch {
        showSnackbar("Could not copy link", "error");
      }
    } finally {
      setShareLoading(false);
    }
  };

  /**
   * Handle closing the success modal
   */
  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  // Loading, error and not-found states
  if (isLoading) return (
    <Container sx={{ py: 4, textAlign: 'center' }}>
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Loading product...</Typography>
    </Container>
  );

  if (error) return (
    <Container sx={{ py: 4 }}>
      <Alert severity="error" sx={{ mb: 2 }}>Failed to load product.</Alert>
      <Button component={Link} to="/products" variant="contained">
        Back to Products
      </Button>
    </Container>
  );

  if (!product) return (
    <Container sx={{ py: 4 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>Product not found.</Alert>
      <Button component={Link} to="/products" variant="contained">
        Back to Products
      </Button>
    </Container>
  );

  return (
    <Container sx={{ py: 4 }}>
      {/* Success Modal (only used when ENABLE_CHECKOUT=true) */}
      <AddToCartSuccessModal
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        product={addedProduct}
        options={modalOptions}
        quantity={quantity}
        personalizationText={personalizationText}
      />

      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
          Home
        </Link>
        <Link
          to="/products"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Products
        </Link>
        <Typography color="text.primary">
          {product.name}
        </Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {product.images && product.images.length > 0 ? (
                <img
                  src={getProductImageUrl(product.images[selectedImage])}
                  alt={product.images[selectedImage]?.alt_text || product.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Typography color="text.secondary">No image available</Typography>
              )}
            </Box>
          </Paper>

          {/* Thumbnail Gallery — product photos first, flagged Etsy marketing slides in their own section */}
          {product.images && product.images.length > 1 && (() => {
            const photoImages = product.images.filter((img) => !img.is_marketing);
            const marketingImages = product.images.filter((img) => img.is_marketing);
            const renderThumb = (img, index) => (
              <ImageListItem
                key={index}
                onClick={() => setSelectedImage(index)}
                sx={{
                  cursor: 'pointer',
                  border: index === selectedImage ? '2px solid #1976d2' : 'none',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={getProductImageUrl(img)}
                  alt={img.alt_text || `${product.name} - view ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </ImageListItem>
            );
            return (
              <>
                <ImageList cols={5} rowHeight={80} sx={{ mb: 2 }}>
                  {photoImages.map((img, i) => renderThumb(img, i))}
                </ImageList>
                {marketingImages.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      More details &amp; sizing
                    </Typography>
                    <ImageList cols={5} rowHeight={80} sx={{ mb: 2 }}>
                      {marketingImages.map((img, i) => renderThumb(img, photoImages.length + i))}
                    </ImageList>
                  </>
                )}
              </>
            );
          })()}
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" component="h1" gutterBottom>
            {product.name}
          </Typography>

          {/* Price Display */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" component="span" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(parseFloat(product.price) || 0)}
            </Typography>
          </Box>

          {/* Short Description */}
          {product.shortDescription && (
            <Typography variant="body1" component="div" sx={{ mb: 3 }}>
              <ReactMarkdown>{product.shortDescription}</ReactMarkdown>
            </Typography>
          )}

          {/* Interactive Product Options */}
          <ProductOptionsSelector
            product={product}
            onSelectionChange={setSelectedOptions}
            ref={optionsSelectorRef}
          />

          {/* Personalization Field */}
          <ProductPersonalization
            product={product}
            value={personalizationText}
            onChange={(e) => setPersonalizationText(e.target.value)}
          />

          {/* Quantity and Add to Cart / Buy on Etsy */}
          <Box sx={{ my: 3 }}>
            {ENABLE_CHECKOUT ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={handleQuantityChange}
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ width: 100 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={handleAddToCart}
                  disabled={!personalizationValid}
                  sx={{ py: 1 }}
                >
                  Add to Cart
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                  disabled={shareLoading}
                  sx={{ py: 1 }}
                >
                  {shareLoading ? 'Copying...' : shareUrl ? 'Copied!' : 'Share'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <BuyOnEtsyButton product={product} fullWidth />
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                  disabled={shareLoading}
                  fullWidth
                >
                  {shareLoading ? 'Copying...' : shareUrl ? 'Copied!' : 'Share / Copy Link'}
                </Button>
              </Box>
            )}
          </Box>

          {/* Product Details Tabs */}
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="product information tabs"
              >
                <Tab label="Description" id="tab-0" />
                <Tab label="Specifications" id="tab-1" />
                {product.careInstructions && <Tab label="Care" id="tab-2" />}
              </Tabs>
            </Box>

            {/* Description Tab */}
            <div role="tabpanel" hidden={tabValue !== 0}>
              {tabValue === 0 && (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body1" component="div">
                    <ReactMarkdown>{product.longDescription || "No detailed description available."}</ReactMarkdown>
                  </Typography>
                </Box>
              )}
            </div>

            {/* Specifications Tab */}
            <div role="tabpanel" hidden={tabValue !== 1}>
              {tabValue === 1 && (
                <Box sx={{ py: 2 }}>
                  <ProductSpecifications product={product} />
                </Box>
              )}
            </div>

            {/* Care Instructions Tab */}
            {product.careInstructions && (
              <div role="tabpanel" hidden={tabValue !== 2}>
                {tabValue === 2 && (
                  <Box sx={{ py: 2 }}>
                    <Typography variant="body1">
                      {product.careInstructions}
                    </Typography>
                  </Box>
                )}
              </div>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductDetail;
